
import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/contexts/notifications';
import { useTeacherNotifications } from '@/hooks/useTeacherNotifications';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface NotificationListProps {
  onClose?: () => void;
}

export const NotificationList: React.FC<NotificationListProps> = ({ onClose }) => {
  // Get notifications from both contexts
  let notifications: any[] = [];
  let teacherNotifications: any[] = [];
  let markAsRead = (_id: string) => {};
  let markTeacherNotificationAsRead = (_id: string) => Promise.resolve(false);
  let processPaymentAction = (_id: string) => {};
  
  try {
    const notificationContext = useNotifications();
    if (notificationContext) {
      notifications = notificationContext.notifications || [];
      markAsRead = notificationContext.markAsRead;
      processPaymentAction = notificationContext.processPaymentAction || ((_id: string) => {});
    }
  } catch (error) {
    console.error("Notification context not available:", error);
  }
  
  try {
    const teacherNotificationContext = useTeacherNotifications();
    if (teacherNotificationContext) {
      teacherNotifications = teacherNotificationContext.notifications || [];
      markTeacherNotificationAsRead = teacherNotificationContext.markAsRead || ((_id: string) => Promise.resolve(false));
    }
  } catch (error) {
    console.error("Teacher notification context not available:", error);
  }
  
  // Combine and sort all notifications by timestamp
  const allNotifications = [
    ...notifications.map(n => ({
      ...n,
      source: 'app',
      formattedTime: formatDistanceToNow(new Date(n.timestamp), { addSuffix: true })
    })),
    ...teacherNotifications.map(n => ({
      ...n,
      source: 'teacher',
      formattedTime: formatDistanceToNow(new Date(n.createdAt || n.date), { addSuffix: true })
    }))
  ].sort((a, b) => {
    const dateA = a.timestamp || a.createdAt || a.date || new Date();
    const dateB = b.timestamp || b.createdAt || b.date || new Date();
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  const handleNotificationClick = async (notification: any) => {
    if (notification.source === 'app') {
      markAsRead(notification.id);
      
      // Handle action if available
      if (notification.hasAction && processPaymentAction) {
        processPaymentAction(notification.id);
        if (onClose) onClose();
      }
    } else if (notification.source === 'teacher') {
      await markTeacherNotificationAsRead(notification.id);
      
      // Handle teacher notification action if needed
      if (notification.metadata?.actionUrl) {
        // Handle action URL if needed
        if (onClose) onClose();
      }
    }
  };

  const renderPriorityBadge = (priority: string) => {
    if (!priority) return null;
    
    let badgeClass = '';
    
    switch(priority) {
      case 'urgent':
      case 'high':
        badgeClass = 'bg-red-500';
        break;
      case 'important':
      case 'medium':
        badgeClass = 'bg-yellow-500';
        break;
      default:
        badgeClass = 'bg-blue-500';
    }
    
    return (
      <Badge className={`${badgeClass} text-white text-xs`}>
        {priority}
      </Badge>
    );
  };
  
  return (
    <ScrollArea className="h-80">
      {allNotifications.length === 0 ? (
        <div className="flex items-center justify-center h-full p-6 text-center">
          <div>
            <p className="text-muted-foreground">No notifications yet</p>
          </div>
        </div>
      ) : (
        <div className="divide-y">
          {allNotifications.map((notification) => {
            const isRead = notification.read || notification.isRead;
            const priority = notification.priority || 
                           (notification.metadata?.priority || 'medium');
            
            return (
              <div
                key={notification.id}
                className={`p-4 ${isRead ? 'bg-background' : 'bg-muted/30'} hover:bg-muted/50 cursor-pointer`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-muted-foreground text-xs mt-1">{notification.message}</p>
                    
                    <div className="flex items-center gap-2 mt-2">
                      {renderPriorityBadge(priority)}
                      <span className="text-xs text-muted-foreground">{notification.formattedTime}</span>
                    </div>
                  </div>
                  
                  {(notification.hasAction || notification.metadata?.actionUrl || notification.metadata?.hasAction) && (
                    <Button variant="ghost" size="sm" className="mt-1 h-8 px-2">
                      <ExternalLink className="h-4 w-4" />
                      <span className="sr-only">Action</span>
                    </Button>
                  )}
                </div>
                
                {notification !== allNotifications[allNotifications.length - 1] && (
                  <Separator className="mt-2" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </ScrollArea>
  );
};
